import ky from 'ky'
import { apiClient } from '@mote/api'
import { delay } from '@mote/helpers'
import type { Chunk } from './Chunk'

type ErrorHandler = (error: UploadQueueError) => void

export class UploadQueue {
  readonly RETRY_DELAY = 1_000

  totalChunks = 0
  chunkFailed = 0
  inProgress = 0

  started: boolean = false
  paused: boolean = false
  completed: boolean = false

  onError: ErrorHandler

  #abortController = new AbortController()
  #apiClient = apiClient.extend({ retry: 0, timeout: false })
  #urlWithId = ''

  #queue: Chunk[] = []
  #startTime: number = 0

  #waitPromise: Promise<void> = Promise.resolve(void 0)
  #waitResolve: ((value?: void) => void) | null = null
  #abortPromise: Promise<void> = Promise.resolve()
  #abortResolve: ((value?: void) => void) | null = null

  constructor(onError: ErrorHandler) {
    this.onError = onError
  }

  start(urlWithId: string) {
    this.#abortPromise = new Promise((r, reject) => {
      this.#abortResolve = reject
    })
    this.#abortPromise.catch(() => {
      /* NOOP */
    })
    this.#waitPromise = new Promise(resolve => {
      this.started = true
      this.#waitResolve = resolve
      this.#urlWithId = urlWithId
      this.#startTime = Date.now()
      this.#startUploads()
    })
  }

  add(chunk: Chunk) {
    this.totalChunks += 1
    this.paused = false
    this.#queue.push(chunk)

    if (!this.started) {
      return
    }

    this.#startUploads()
  }

  complete() {
    this.#resolveIfNotInProgress()
    this.completed = true
    return this.#waitPromise.then(() => {
      this.abort()
    })
  }

  pause() {
    this.#resolveIfNotInProgress()
    this.paused = true
    return this.#waitPromise.then(() => {
      this.#waitPromise = new Promise(resolve => {
        this.#waitResolve = resolve
      })
    })
  }

  abort() {
    this.#queue = []
    this.#waitPromise = Promise.resolve(void 0)
    this.#waitResolve = null
    this.started = false
    this.inProgress = 0
    this.#abortController.abort()
    if (this.#abortResolve) {
      this.#abortResolve()
    }
    this.#abortPromise = Promise.resolve(void 0)
    this.#abortResolve = null
  }

  get introspection() {
    return {
      totalChunks: this.totalChunks,
      chunkFailed: this.chunkFailed,
      underflow: Number(this.underflow.toPrecision(2)),
      failurePct: this.totalChunks > 0 ? Math.round(this.underflow * 100) : 0
    }
  }

  get underflow() {
    return this.chunkFailed / this.totalChunks
  }

  #startUploads() {
    const current = this.#queue.splice(0, this.#queue.length)
    this.inProgress += current.length
    for (const chunk of current) {
      this.#startUpload(chunk)
    }
  }

  #startUpload(chunk: Chunk) {
    return this.#upload(chunk)
      .then(() => {
        this.inProgress--
        if ((this.completed || this.paused) && this.inProgress === 0) {
          this.#resolveIfNotInProgress()
        } else {
          this.#startUploads()
        }
      })
      .catch(() => {
        /*NOOP*/
      })
  }

  async #upload(chunk: Chunk): Promise<void> {
    try {
      await this.#apiClient.post(
        `${this.#urlWithId}/${chunk.id}?size=${chunk.size}`,
        {
          body: chunk.blob,
          signal: this.#abortController.signal
        }
      )
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return
      }

      if (e instanceof ky.HTTPError) {
        switch (e.response.status) {
          case 410:
            this.abort()
            return this.onError(
              new NonRecoverableUploadQueueError(e.response.status)
            )
          case 417:
            return this.onError(new ForceStopUploadQueueError())
        }
      }

      if (chunk.wasAlreadyRetried) {
        this.chunkFailed++
      }

      chunk.retriesAttempted++
      await Promise.race([delay(this.RETRY_DELAY), this.#abortPromise])
      return this.#upload(chunk)
    }
  }

  #resolveIfNotInProgress() {
    if (this.inProgress === 0 && this.#waitResolve) {
      this.#waitResolve()
    }
  }
}

class UploadQueueError extends Error {
  constructor(message?: string) {
    super(message)
    Object.setPrototypeOf(this, UploadQueueError.prototype)
  }
}

export class NonRecoverableUploadQueueError extends UploadQueueError {
  constructor(code: number) {
    super(
      `Non recoverable upload queue error, upload queue has been aborted: ${code}`
    )
    Object.setPrototypeOf(this, NonRecoverableUploadQueueError.prototype)
  }
}

export class ForceStopUploadQueueError extends UploadQueueError {
  constructor() {
    super('Upload queue has been stopped')
    Object.setPrototypeOf(this, ForceStopUploadQueueError.prototype)
  }
}

export type ChunkUploadErrorCode =
  | 'underflow'
  | 'max_chunks'
  | 'other'
  | 'timeout'

export class ChunkUploadError extends UploadQueueError {
  readonly #displayedAfterNSeconds: number
  readonly #errorCode: ChunkUploadErrorCode
  readonly #introspection: UploadQueueIntrospection

  constructor(
    introspection: UploadQueueIntrospection,
    displayedAfterNSeconds: number,
    errorCode: ChunkUploadErrorCode = 'other'
  ) {
    super('Chunk upload error')
    this.#introspection = introspection
    this.#displayedAfterNSeconds = displayedAfterNSeconds
    this.#errorCode = errorCode
    Object.setPrototypeOf(this, ChunkUploadError.prototype)
  }

  toJSON() {
    return {
      ...this.#introspection,
      displayedAfterNSeconds: this.#displayedAfterNSeconds,
      errorCode: this.#errorCode
    }
  }
}

export type UploadQueueIntrospection = InstanceType<
  typeof UploadQueue
>['introspection']
